/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useState } from 'react'

import { GMultiSelect, GSection, GTooltip } from 'components/basic-blocks'

import { AiProtection } from './components/ai-protection'
import { DisabledProtection } from './components/disabled-protection/disabled-protection'
import { Header } from './components/header'
import { ProtectionChecks } from './components/protection-checks'

import { ProtectionChecksType, ProtectionPanelProps } from './types'

import {
	FacebookAdsCampaignDTO,
	GoogleAdsCampaignDTO,
	ProtectionInfoDetectionType,
	ProtectionInfoDTO
} from 'api-models'
import {
	RenderCountryLabel,
	RenderCountrySelectedLabels
} from 'components/basic-blocks/g-multiselect/utils'
import { GRadio } from 'components/basic-blocks/g-radio'
import { GNewTooltip } from 'components/basic-blocks/g-tooltip/g-new-tooltip'
import { CountriesList } from 'config/countries'
import { useStores } from 'hooks'
import { useAsset } from 'pages/workspace/asset/asset-base'
import { HelpCircle as RiQuestionLine } from 'components/icons'
import { toast } from 'react-toastify'
import { BlockingMode } from './components/blocking-mode'
import { DetectionMode } from './components/detection-mode'
import {
	getAiProtectionValue,
	getProtectionChecksValues,
	prepareProtectionPayload
} from './utils'
import { HelpCircle as AiOutlineQuestionCircle } from 'components/icons'
import { useTrackPostHogEvent } from '../../../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../../../utils/posthog-events'

export const ProtectionPanel = ({
	level,
	protection,
	target,
	inheritName,
	platform
}: ProtectionPanelProps) => {
	const { assetStore } = useStores()
	const {
		setAccountProtection,
		setAdAccountProtection,
		setCampaignProtection
	} = assetStore
	const { accountId, assetId } = useAsset()
	const trackPostHogEvent = useTrackPostHogEvent()

	const [aiProtection, setAiProtection] = useState(() =>
		getAiProtectionValue({
			level,
			enabled: protection?.enabled,
			inherit: protection?.inherit
		})
	)
	const [protectionChecksOptions, setProtectionChecksOptions] =
		useState<ProtectionChecksType>(() =>
			getProtectionChecksValues({
				protection
			})
		)
	const [detectionType, setDetectionType] = useState(
		() => protection?.detection_type || ProtectionInfoDetectionType.AI
	)
	const [detectionMode, setDetectionMode] = useState(() =>
		String(protection?.mode || 3)
	)
	// DEV-2422 §1.1 — `disrupt` is no longer an accepted value, but stored rows still
	// hold it until the one-off backfill runs. Read one as Block, or every save from
	// this (flag-off) page 400s on the `isIn(['monitoring', 'block'])` validators.
	const [blockingMode, setBlockingMode] = useState<
		ProtectionInfoDTO['blocking_mode']
	>(() => (protection?.blocking_mode === 'monitoring' ? 'monitoring' : 'block'))
	const [saveLoading, setSaveLoading] = useState(false)
	const [errors, setErrors] = useState<Record<string, string> | null>(null)

	useEffect(() => {
		setAiProtection(() =>
			getAiProtectionValue({
				level,
				enabled: protection?.enabled,
				inherit: protection?.inherit
			})
		)
		setProtectionChecksOptions(() =>
			getProtectionChecksValues({
				protection
			})
		)
		setDetectionType(
			protection?.detection_type || ProtectionInfoDetectionType.AI
		)
		setDetectionMode(String(protection?.mode || 3))
		setBlockingMode(
			protection?.blocking_mode === 'monitoring' ? 'monitoring' : 'block'
		)
	}, [level, protection])

	const handleSave = async () => {
		try {
			setSaveLoading(true)

			if (
				aiProtection === 'enabled' &&
				protectionChecksOptions.region &&
				protectionChecksOptions.region.length === 0
			) {
				setErrors({ region: 'Select at least one region' })
				const element = document.getElementsByName(
					'geo-protection-countries'
				)[0]
				element.focus()
				return
			}

			const preparedPayload = prepareProtectionPayload({
				aiProtection,
				detectionType,
				detectionMode: Number(detectionMode) as ProtectionInfoDTO['mode'],
				blockingMode: blockingMode,
				protectionChecks: protectionChecksOptions
			})

			if (level === 'website') {
				await setAccountProtection(accountId!, assetId!, preparedPayload)
			} else if (level === 'ad-account') {
				await setAdAccountProtection(accountId!, target?.id!, preparedPayload)
			} else {
				const castedCampaign =
					platform === 'GADS'
						? (target as GoogleAdsCampaignDTO)
						: (target as FacebookAdsCampaignDTO)
				await setCampaignProtection(
					accountId!,
					castedCampaign.ad_account_id!,
					castedCampaign?.id!,
					preparedPayload,
					platform
				)
			}

			const detectionModeMap: Record<string, string> = {
				'1': 'quiet',
				'2': 'softer',
				'3': 'normal',
				'4': 'stricter',
				'5': 'aggressive'
			}

			trackPostHogEvent(PostHogEvent.SAVE_AI_PROTECTION_SETTINGS, PostHogPath.AI_AND_CUSTOMRULES, {
				status: aiProtection === 'enabled' ? 'enabled' : 'disabled',
				fraud_detection: detectionType === ProtectionInfoDetectionType.AI ? 'AI' : 'Custom',
				custom_tuning: detectionType === ProtectionInfoDetectionType.CUSTOM_TUNING ? detectionModeMap[detectionMode] : null,
				protective_actions: blockingMode
			})
		} catch (e) {
			toast.error('Error while saving protection')
		} finally {
			setSaveLoading(false)
		}
	}

	const clearError = (key: string) => {
		setErrors((prev) => {
			const copy = { ...prev }
			delete copy[key]
			return copy
		})
	}

	return (
		<GSection containerClassName="h-full w-full !mb-0">
			<Header loading={saveLoading} handleSave={handleSave} />
			<AiProtection
				level={level}
				aiProtection={aiProtection}
				setAiProtection={setAiProtection}
				inheritName={inheritName}
			/>
			{aiProtection === 'disabled' && <DisabledProtection />}
			{aiProtection === 'enabled' && (
				<div className="flex flex-col gap-6">
					<div>
						<div className="text-t-default text-md flex flex-row items-center gap-x-1 ">
							<span>Advertising region</span>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
								content="Select you main advertising region. The system will be more restrictive for traffic coming outside of the countries you’re advertising in.">
									<AiOutlineQuestionCircle className="w-4 h-4 text-t-gray-hold" />
							</GTooltip>
						</div>
						<GMultiSelect<{ code: string; name: string }>
							name="geo-protection-countries"
							loopKey={'code'}
							value={CountriesList.filter((c) =>
								protectionChecksOptions.region?.includes(c.code)
							)}
							options={CountriesList}
							placeholder="Select one region"
							onChange={(val) => {
								setProtectionChecksOptions((prev) => {
									return {
										...prev,
										region: [...val.map((v) => v.code)]
									}
								})
								clearError('region')
							}}
							renderSelected={(list) => (
								<RenderCountrySelectedLabels options={list} />
							)}
							renderLabel={(list) => <RenderCountryLabel option={list} />}
							error={errors?.region}
						/>
					</div>
					<div>
						<div className="text-t-default text-md flex flex-row items-center gap-x-1 mb-4">
							<span className="font-medium">Fraud detection tuning</span>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
								content="Select how you want your automated protection to work.">
								<div>
									<AiOutlineQuestionCircle className="w-4 h-4 text-t-gray-hold" />
								</div>
							</GTooltip>
						</div>
						<div className="flex flex-col space-y-2">
							<GRadio
								name="ai"
								checked={detectionType === ProtectionInfoDetectionType.AI}
								label="AI driven"
								onChange={(e) => setDetectionType(e.target.value)}
								value="AI"
								labelClass="text-t-default font-normal"
								helpToolTip="Let ClickGUARD AI dynamically adjust the protection settings based on traffic quality and behavior."
							/>
							<GRadio
								name="custom_tuning"
								checked={
									detectionType === ProtectionInfoDetectionType.CUSTOM_TUNING
								}
								label="Custom tuning"
								onChange={(e) => setDetectionType(e.target.value)}
								value="CUSTOM_TUNING"
								labelClass="text-t-default font-normal"
								helpToolTip="Manually select on which problems should the system be more or less focused."
							/>
						</div>
					</div>
					{detectionType === ProtectionInfoDetectionType.CUSTOM_TUNING && (
						<>
							<DetectionMode
								detectionMode={detectionMode}
								setDetectionMode={setDetectionMode}
							/>
							<ProtectionChecks
								checks={protectionChecksOptions}
								setProtectionChecksOptions={setProtectionChecksOptions}
							/>
						</>
					)}
					<BlockingMode
						blockingMode={blockingMode}
						setBlockingMode={setBlockingMode}
					/>
				</div>
			)}
		</GSection>
	)
}
